import React, { useState } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Calendar, User, Mail, Phone, MessageSquare, Send, Sparkles, X } from 'lucide-react';
import { crmAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import CountryCodeSelect from './CountryCodeSelect';

const BookDemoModal = ({ open, onOpenChange, cardUsername, cardOwnerName }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+91',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast({
        title: "Name required",
        description: "Please enter your name",
        variant: "destructive"
      });
      return;
    }

    if (!formData.email.trim() && !formData.phone.trim()) {
      toast({
        title: "Contact required",
        description: "Please enter your email or phone number",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const demoData = {
        name: formData.name.trim(),
        email: formData.email.trim() || null,
        phone: formData.phone.trim() ? `${formData.countryCode}${formData.phone.trim()}` : null,
        message: formData.message.trim() || null,
        source: cardUsername ? "Demo Request - Bio Page" : "Demo Request - Website"
      };

      // Use default username 'mraja' for general demo requests from website
      const targetUsername = cardUsername || 'mraja';
      await crmAPI.submitDemoRequest(targetUsername, demoData);
      
      setIsSuccess(true);
      
      toast({
        title: "Demo Request Sent!",
        description: `${cardOwnerName || 'The owner'} will contact you soon.`
      });

      // Reset form after short delay
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          countryCode: '+91',
          message: ''
        });
        setIsSuccess(false);
        onOpenChange(false);
      }, 2000);

    } catch (error) {
      console.error('Error submitting demo request:', error);
      toast({
        title: "Error",
        description: "Failed to submit demo request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        name: '',
        email: '',
        phone: '',
        countryCode: '+91',
        message: ''
      });
      setIsSuccess(false);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-md mx-auto p-0 overflow-hidden rounded-2xl max-h-[90vh] overflow-y-auto">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-5 text-white sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-white/20 backdrop-blur rounded-full flex items-center justify-center flex-shrink-0">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-bold">Book Your Free Demo</h2>
                <p className="text-sm text-white/80 truncate">Connect with {cardOwnerName || 'us'}</p>
              </div>
            </div>
            <button 
              onClick={handleClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              data-testid="close-demo-modal"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {isSuccess ? (
          // Success State
          <div className="px-5 py-10 text-center">
            <div className="w-20 h-20 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Request Sent!</h3>
            <p className="text-gray-600">We'll get back to you shortly.</p>
          </div>
        ) : (
          // Form
          <form onSubmit={handleSubmit} className="px-5 py-5 space-y-4">
            {/* Name Input */}
            <div className="space-y-1.5">
              <Label htmlFor="demo-name" className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                <User className="w-4 h-4 text-amber-500" />
                Your Name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="demo-name"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full h-12 text-base border-gray-200 focus:border-amber-500 focus:ring-amber-500 rounded-xl"
                data-testid="demo-name-input"
              />
            </div>

            {/* Email Input */}
            <div className="space-y-1.5">
              <Label htmlFor="demo-email" className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                <Mail className="w-4 h-4 text-amber-500" />
                Email
              </Label>
              <Input
                id="demo-email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full h-12 text-base border-gray-200 focus:border-amber-500 focus:ring-amber-500 rounded-xl"
                data-testid="demo-email-input"
              />
            </div>

            {/* Phone Input */}
            <div className="space-y-1.5">
              <Label htmlFor="demo-phone" className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                <Phone className="w-4 h-4 text-amber-500" />
                Phone Number
              </Label>
              <div className="flex gap-2">
                <CountryCodeSelect
                  value={formData.countryCode}
                  onChange={(e) => handleInputChange('countryCode', e.target.value)}
                  className="w-24 h-12 text-sm rounded-xl"
                />
                <Input
                  id="demo-phone"
                  type="tel"
                  inputMode="numeric"
                  placeholder="9876543210"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value.replace(/[^\d]/g, ''))}
                  className="flex-1 h-12 text-base border-gray-200 focus:border-amber-500 focus:ring-amber-500 rounded-xl"
                  data-testid="demo-phone-input"
                />
              </div>
            </div>

            {/* Message Input */}
            <div className="space-y-1.5">
              <Label htmlFor="demo-message" className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                <MessageSquare className="w-4 h-4 text-amber-500" />
                What are you interested in? <span className="text-gray-400 font-normal">(Optional)</span>
              </Label>
              <textarea
                id="demo-message"
                placeholder="Tell us about your requirements..."
                value={formData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                className="w-full px-4 py-3 text-base border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                rows={3}
                maxLength={500}
                data-testid="demo-message-input"
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-12 text-base bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold rounded-xl shadow-lg shadow-amber-500/25 active:scale-[0.98] transition-all"
              data-testid="submit-demo-btn"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Book Free Demo
                </>
              )}
            </Button>

            {/* Privacy note */}
            <p className="text-xs text-center text-gray-400 pt-2">
              Your information will only be shared with {cardOwnerName || 'the card owner'}.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookDemoModal;
